/**
 * Cut a simple polygon with an infinite line and get the pieces on each side.
 *
 * `clipPolygonByHalfPlane` (shared-lib/split-level/raisedFootprint.js) answers
 * the easier question — "what is left of this polygon on one side" — with a
 * Sutherland-Hodgman clip, which folds a multi-piece result into ONE pinched
 * ring. The room split needs the piece COUNT: a U-shaped room cut across its
 * mouth leaves two pieces on one side, and minting three rooms out of a
 * two-room gesture is exactly the outcome the split must refuse. So this
 * module decomposes instead of clipping.
 *
 * Exact and vertex-preserving: a vertex the cut does not touch comes out of
 * the same object it went in as (by value), so a wall carried verbatim to one
 * side still lies on that side's boundary to the last bit. That rules out
 * routing this through `polygon-clipping`, which re-rounds every coordinate.
 *
 * Geometry is the X-Z floor plane (Y is up). Signed distance to the line is
 * `d(p) = p·n - c` for a unit normal `n`; `front` is `d > 0`, `back` is
 * `d < 0`. Winding is read from the input, never assumed — the room polygons
 * are CCW-from-above (CLAUDE.md), but nothing here depends on that.
 *
 * Algorithm (ring walk, the standard polygon/line slice):
 *   1. Insert a vertex at every real crossing, so no edge straddles the line.
 *   2. For one side, take the maximal runs of vertices on that side. Each run
 *      starts and ends ON the line (the inserted crossings guarantee it).
 *   3. Close the runs along the line: from a run's exit point, travel in the
 *      one direction that keeps that side's interior to the correct hand, and
 *      link to the nearest run entry point ahead. Chaining those links yields
 *      one closed loop per piece.
 */

const ON_LINE_EPS = 1e-7; // |d| below this is ON the line, not across it
const MIN_PIECE_AREA_M2 = 1e-6; // a loop smaller than this is numerical dust

/** Shoelace signed area of an `{x,z}[]` ring. Sign encodes winding. */
function signedAreaXZ(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.z - q.x * p.z;
  }
  return a / 2;
}

/** Signed distance from `p` to the line, snapped to exactly 0 inside the band. */
function signedDistance(p, n, c, eps) {
  const d = p.x * n.x + p.z * n.z - c;
  return Math.abs(d) <= eps ? 0 : d;
}

/**
 * The input ring with a vertex inserted wherever an edge strictly crosses the
 * line. Returns `[{ p, d }]` — `p` is the ORIGINAL point object for untouched
 * vertices, so the caller's coordinates survive byte-for-byte.
 */
function augmentRing(poly, n, c, eps) {
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const A = poly[i];
    const B = poly[(i + 1) % poly.length];
    const dA = signedDistance(A, n, c, eps);
    const dB = signedDistance(B, n, c, eps);
    out.push({ p: A, d: dA });
    if ((dA > 0 && dB < 0) || (dA < 0 && dB > 0)) {
      const t = dA / (dA - dB);
      out.push({
        p: { x: A.x + t * (B.x - A.x), z: A.z + t * (B.z - A.z) },
        d: 0,
      });
    }
  }
  return out;
}

/**
 * Maximal circular runs of vertices with `side * d >= 0`. Each run is returned
 * as a `{x,z}[]` chain whose first and last points lie on the line.
 * Returns `null` when EVERY vertex is on that side (the polygon is not cut).
 */
function chainsForSide(ring, side) {
  const keep = ring.map((v) => side * v.d >= 0);
  if (keep.every(Boolean)) return null;
  if (!keep.some(Boolean)) return [];

  // Start the circular walk at a run boundary so runs never wrap the array end.
  let start = 0;
  while (!(keep[start] && !keep[(start - 1 + ring.length) % ring.length])) {
    start = (start + 1) % ring.length;
  }

  const chains = [];
  let current = null;
  for (let k = 0; k < ring.length; k++) {
    const i = (start + k) % ring.length;
    if (keep[i]) {
      if (!current) current = [];
      current.push(ring[i].p);
    } else if (current) {
      chains.push(current);
      current = null;
    }
  }
  if (current) chains.push(current);
  return chains;
}

/**
 * Direction to travel ALONG the line when closing `side`'s chains.
 *
 * With a positive shoelace area the interior sits to the rot+90 side of the
 * travel direction (`rot90(T) = (-T.z, T.x)`); the interior of this piece is
 * the `side * n` half-plane, so `rot90(T) = side * n`, i.e.
 * `T = side * (n.z, -n.x)`. A negatively-wound input reverses it.
 */
function closureDirection(n, side, areaSign) {
  const s = side * areaSign;
  return { x: s * n.z, z: -s * n.x };
}

/** Link each chain's exit point to the nearest chain entry point ahead of it
 *  along `T`, then walk the links into closed loops. */
function closeChains(chains, T, eps) {
  const t = (p) => p.x * T.x + p.z * T.z;
  const exits = chains.map((ch) => t(ch[ch.length - 1]));
  const entries = chains.map((ch) => t(ch[0]));

  // Nearest entry ahead (ties and self-closure allowed via the epsilon slack).
  const nextOf = chains.map((_, i) => {
    let best = -1;
    let bestT = Infinity;
    for (let j = 0; j < chains.length; j++) {
      if (entries[j] < exits[i] - eps) continue;
      if (entries[j] < bestT) {
        bestT = entries[j];
        best = j;
      }
    }
    // A closed boundary always has an entry ahead; fall back to the lowest
    // entry (wrap) rather than dropping the piece on a numerical tie.
    if (best === -1) {
      bestT = Infinity;
      for (let j = 0; j < chains.length; j++) {
        if (entries[j] < bestT) {
          bestT = entries[j];
          best = j;
        }
      }
    }
    return best;
  });

  const used = new Array(chains.length).fill(false);
  const loops = [];
  for (let i = 0; i < chains.length; i++) {
    if (used[i]) continue;
    const loop = [];
    let k = i;
    while (!used[k]) {
      used[k] = true;
      loop.push(...chains[k]);
      k = nextOf[k];
      if (k === -1) break;
    }
    loops.push(loop);
  }
  return loops;
}

/** Drop consecutive duplicate points (the chain joins land on the same point
 *  whenever a run ends where the next one begins). */
function dedupe(loop, eps) {
  const out = [];
  for (const p of loop) {
    const last = out[out.length - 1];
    if (
      last &&
      Math.abs(last.x - p.x) <= eps &&
      Math.abs(last.z - p.z) <= eps
    ) {
      continue;
    }
    out.push(p);
  }
  while (
    out.length > 1 &&
    Math.abs(out[0].x - out[out.length - 1].x) <= eps &&
    Math.abs(out[0].z - out[out.length - 1].z) <= eps
  ) {
    out.pop();
  }
  return out;
}

function piecesForSide(poly, ring, n, side, eps, minArea) {
  let chains = chainsForSide(ring, side);
  if (chains === null) return [poly.map((p) => p)]; // wholly on this side
  // A single on-line vertex is the polygon TOUCHING the line, not entering
  // this side. Drop it before the pairing, where it would otherwise consume
  // a real chain's partner.
  chains = chains.filter((ch) => ch.length > 1);
  if (chains.length === 0) return [];
  const areaSign = signedAreaXZ(poly) >= 0 ? 1 : -1;
  const T = closureDirection(n, side, areaSign);
  return closeChains(chains, T, eps)
    .map((loop) => dedupe(loop, eps))
    .filter(
      (loop) => loop.length >= 3 && Math.abs(signedAreaXZ(loop)) >= minArea
    );
}

/**
 * Split `poly` by the line `p·n = c`.
 *
 * @param {{x:number,z:number}[]} poly simple ring, either winding
 * @param {{x:number,z:number}} n unit normal of the cut line
 * @param {number} c line offset along `n`
 * @param {{ eps?: number, minArea?: number }} [opts]
 * @returns {{ front: Array<{x,z}[]>, back: Array<{x,z}[]> }} pieces with
 *   `d > 0` and `d < 0` respectively, each preserving the input's winding.
 */
function splitPolygonByLine(poly, n, c, opts = {}) {
  const eps = opts.eps ?? ON_LINE_EPS;
  const minArea = opts.minArea ?? MIN_PIECE_AREA_M2;
  if (!Array.isArray(poly) || poly.length < 3) return { front: [], back: [] };
  const ring = augmentRing(poly, n, c, eps);
  return {
    front: piecesForSide(poly, ring, n, 1, eps, minArea),
    back: piecesForSide(poly, ring, n, -1, eps, minArea),
  };
}

module.exports = {
  ON_LINE_EPS,
  MIN_PIECE_AREA_M2,
  signedAreaXZ,
  splitPolygonByLine,
};
